import React, { useState, useEffect, useRef } from 'react'
import { gsap } from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'

gsap.registerPlugin(ScrollTrigger)

const services = [
  { name: 'Hair Couture', desc: 'Precision cuts, editorial styling, colour artistry' },
  { name: 'Makeup Artistry', desc: 'Bridal, occasion and editorial makeup' },
  { name: 'Body & Skin', desc: 'Advanced facials, skin science and body rituals' },
  { name: 'Bridal Concierge', desc: 'Complete wedding and occasion service' },
]

export default function Reservations() {
  const [form, setForm] = useState({
    name: '',
    service: '',
    date: '',
    time: '',
    notes: '',
  })
  const [submitted, setSubmitted] = useState(false)
  const ref = useRef<HTMLElement>(null)
  const formRef = useRef<HTMLFormElement>(null)

  useEffect(() => {
    if (!ref.current) return

    const ctx = gsap.context(() => {
      gsap.fromTo(
        ref.current.querySelector('.reservations__content'),
        { opacity: 0, y: 50 },
        {
          opacity: 1,
          y: 0,
          duration: 1,
          ease: 'power3.out',
          scrollTrigger: {
            trigger: ref.current,
            start: 'top 75%',
            once: true,
          },
        }
      )

      gsap.fromTo(
        ref.current.querySelectorAll('.reservations__info'),
        { opacity: 0, x: -40 },
        {
          opacity: 1,
          x: 0,
          duration: 1,
          stagger: 0.15,
          ease: 'power3.out',
          scrollTrigger: {
            trigger: ref.current,
            start: 'top 75%',
            once: true,
          },
        }
      )
    }, ref.current)

    return () => ctx.revert()
  }, [])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value })
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setSubmitted(true)

    // Build WhatsApp message
    const message = `Hello Aure Studio, I'd like to book an appointment.%0A%0AName: ${form.name}%0AService: ${form.service}%0APreferred Date: ${form.date}%0APreferred Time: ${form.time}%0ANotes: ${form.notes || 'None'}`
    const waUrl = `https://wa.me/919746465574?text=${message}`

    // Open WhatsApp
    window.open(waUrl, '_blank')

    // Reset form
    setTimeout(() => {
      setForm({ name: '', service: '', date: '', time: '', notes: '' })
      setSubmitted(false)
    }, 3000)
  }

  return (
    <section ref={ref} className="reservations" id="reservations">
      <div className="reservations__inner">
        <div className="reservations__content">
          <span className="reservations__label">Reservations</span>
          <h2 className="reservations__title">
            Book Your <em>Private Appointment</em>
          </h2>
          <p className="reservations__text">
            Share a few details and your request is sent directly to our concierge
            on WhatsApp. We confirm every appointment personally.
          </p>

          <form ref={formRef} className="reservations__form" onSubmit={handleSubmit}>
            <div className="reservations__field">
              <label htmlFor="name">Your name</label>
              <input
                type="text"
                id="name"
                name="name"
                value={form.name}
                onChange={handleChange}
                required
                placeholder="Your full name"
              />
            </div>

            <div className="reservations__field">
              <label htmlFor="service">Ritual</label>
              <select
                id="service"
                name="service"
                value={form.service}
                onChange={handleChange}
                required
              >
                <option value="">Select a service</option>
                <option>Hair Couture</option>
                <option>Makeup Artistry</option>
                <option>Body & Skin</option>
                <option>Bridal Concierge</option>
              </select>
            </div>

            <div className="reservations__row">
              <div className="reservations__field">
                <label htmlFor="date">Preferred date</label>
                <input
                  type="date"
                  id="date"
                  name="date"
                  value={form.date}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="reservations__field">
                <label htmlFor="time">Preferred time</label>
                <input
                  type="time"
                  id="time"
                  name="time"
                  value={form.time}
                  onChange={handleChange}
                  required
                />
              </div>
            </div>

            <div className="reservations__field">
              <label htmlFor="notes">Notes (optional)</label>
              <textarea
                id="notes"
                name="notes"
                value={form.notes}
                onChange={handleChange}
                rows={3}
                placeholder="Any special requests or preferences..."
              />
            </div>

            <button type="submit" className="reservations__submit">
              {submitted ? '✓ Sent via WhatsApp' : 'Send Request via WhatsApp'}
            </button>
          </form>

          <a
            href="https://wa.me/919746465574?text=Hello%20Aure%20Studio%2C%20I%27d%20like%20to%20book%20an%20appointment."
            className="reservations__chat"
            target="_blank"
            rel="noopener noreferrer"
          >
            Chat with Aure Studio on WhatsApp
          </a>
        </div>

        <aside className="reservations__info">
          <div className="reservations__info-block">
            <span className="reservations__info-icon">📞</span>
            <div>
              <strong>Concierge</strong>
              <a href="tel:+919746465574">+91 9746465574</a>
            </div>
          </div>
          <div className="reservations__info-block">
            <span className="reservations__info-icon">🕒</span>
            <div>
              <strong>Hours</strong>
              <span>10:00 AM – 1:00 AM, Every Day</span>
            </div>
          </div>
          <div className="reservations__info-block">
            <span className="reservations__info-icon">📍</span>
            <div>
              <strong>Atelier</strong>
              <span>Aure Unisex Studio, Palarivattom, Ernakulam, Kerala 682025</span>
            </div>
          </div>
        </aside>
      </div>
    </section>
  )
}